import { ReactNode, useState, useRef } from "react";
import {
  Flex,
  InputGroup,
  InputLeftElement,
  forwardRef,
  Link,
  InputRightElement,
  Select,
  Text,
  Input as ChakraInput,
} from "@chakra-ui/react";
import { IoSearchOutline, IoCalendarOutline } from "react-icons/io5";
import DatePicker, { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import { DownloadIcon } from "@/styles/icons";
import { Input } from "../Forms/Input";
import { format } from "date-fns";
import { DownloadCSVProps } from "@/models";
import { Button, Loading, Modal } from "barte-design-system";
interface FilterProps {
  placeholder: string;
  loading?: boolean;
  filterTable?: (searchParam: string) => void;
  children?: ReactNode;
  searchFunction?: (filter: string) => void;
  setStartDate?: (start: Date) => void;
  setEndDate?: (end: Date) => void;
  startDate?: Date;
  endDate?: Date;
  isVisibleDatepicker?: boolean;
  isVisibleSearchButton?: boolean;
  setFilterParams?: (filter: string) => void;
  getListFilter?: any;
  filterParams?: string;
  onDownloadCSV?: (data: DownloadCSVProps) => void;
}

function Filter({
  filterTable,
  searchFunction,
  placeholder,
  loading,
  children,
  setStartDate,
  setEndDate,
  startDate,
  endDate,
  isVisibleDatepicker = false,
  isVisibleSearchButton = true,
  setFilterParams,
  getListFilter,
  filterParams,
  onDownloadCSV,
}: FilterProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const inputRef = useRef(null);
  const inputEmailRef = useRef(null);
  const user = JSON.parse(localStorage.getItem("barte-user-info"));
  registerLocale("ptBR", ptBR);

  const onMonthChange = (dates: any) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const CustomInput = forwardRef(({ value }: any, ref) => (
    <Button
      onClick={() => setIsDatePickerOpen(true)}
      ref={ref}
      variant="outline"
      iconLeft={ value ?  <IoCalendarOutline color="gray.300" size={20}/> : ""}
    >
      {value ? value : <IoCalendarOutline color="gray.300" size={20}/>}

    </Button>
  ));

  const handleSearch = () => {
    let searchText = inputRef.current.value;
    if (filterTable) {
      filterTable(searchText);
      return;
    }
    if (searchFunction) {
      searchFunction(searchText);
      return;
    }
  };

  const clearFilters = (e: any) => {
    e.preventDefault();
    inputRef.current.value = "";
    if (setStartDate) {
      setStartDate(null);
    }
    if (setEndDate) {
      setEndDate(null);
    }
    handleSearch();
  };

  const handleDownloadClick = () => {
    setIsOpen(true);
  };

  const handleConfirmDownload = () => {
    if (onDownloadCSV) {
      const formatData = {
        expirationDateInitial: startDate ? format(startDate, "yyyy-MM-dd") : "",
        expirationDateFinal: endDate ? format(endDate, "yyyy-MM-dd") : "",
        recipient: inputEmailRef.current.value
      }
      onDownloadCSV(formatData);
    }
    setIsOpen(false);
  };

  return (
    <>
      <Flex
        bg="gray.25"
        p={2}
        borderRadius={8}
        w="100%"
        gap="0.3px"
        align="center"
      >
        {getListFilter &&
          <Select
            fontSize="14px"
            w="130px"
            zIndex="3"
            height={35}
            defaultValue="name"
            backgroundColor="rgba(230, 233, 240, 1)"
            border="none"
            borderRadius="6px 0 0 6px"
            onChange={(e) => setFilterParams(e.target.value)}
          >
            {getListFilter?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        }

        <InputGroup maxW={500} bg="white">
          <InputLeftElement
            pointerEvents="none"
            children={<IoSearchOutline color="gray.300" size={20} />}
            height={35}
          />
          <ChakraInput
            placeholder={placeholder}
            type="search"
            fontSize="1rem"
            borderColor="rgba(230, 233, 240, 1)"
            borderRadius="0 4px 4px 0"
            height={35}
            ref={inputRef}
          />
          <InputRightElement maxW="70px" w="100%" mr="2">
            {loading &&
              <Loading type="icon" />
            }
          </InputRightElement>
        </InputGroup>
        {children}

        <Flex gap="10px" ml="10px" align="center">
          <Flex
            as="button"
            align="center"
            gap="2"
            bgColor="gray.49"
            borderRadius={8}
            px="4"
            onClick={() => handleSearch()}
            cursor="pointer"
            _hover={{ bgColor: "gray.200" }}
          >
            {isVisibleSearchButton && (
              <Flex
                h="35px"
                align="center"
                gap="10px"
              >
                <IoSearchOutline color="gray.300" size={20} />
                <Text>
                  Buscar
                </Text>
              </Flex>
            )}
          </Flex>

          {isVisibleDatepicker && (
            <>
              <DatePicker
                locale="ptBR"
                _hover={{ bgColor: "gray.200" }}
                selected={startDate}
                onChange={(date) => onMonthChange(date)}
                startDate={startDate}
                endDate={endDate}
                onInputClick={() => setIsDatePickerOpen(true)}
                onClickOutside={() => setIsDatePickerOpen(false)}
                open={isDatePickerOpen}
                customInput={<CustomInput />}
                selectsRange
                dateFormat="dd/MM/yyyy"
              />
              <a href="#" onClick={clearFilters}>
                <Link color="blue.50">Limpar</Link>
              </a>
            </>
          )}
        </Flex>
      </Flex>

      <Modal
        isOpen={isOpen}
        title="Baixar relatório .csv"
        onClose={() => setIsOpen(false)}
        footer={{
          primaryButton: { children: "Ok", onClick: handleConfirmDownload },
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
          <p>O relatório será gerado com os filtros que estão aplicados na visualização atual e ficará disponível para download na Central de notificações.</p>
          <input
            type="email"
            placeholder="seu-email@dominio.com"
            name="email"
            ref={inputEmailRef}
            defaultValue={user.email}
            style={{ border: "1px solid #d9d9d9", borderRadius: "4px", padding: "8px 12px", fontSize: "14px", width: "100%" }}
          />
        </div>
      </Modal>
    </>
  );
}

export { Filter };
